import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApmService } from '@elastic/apm-rum-angular';
import { Store } from '@ngrx/store';
import { CurrentAuthenticationState, SsoUserInfo, Token, OAuthError } from '@warema/shared/authentication/models';
import { AuthenticationEnvironment, ENVIRONMENT, StorageConstants, User, UserType } from '@warema/shared/models';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, first, lastValueFrom, Observable, skip, Subscription, take } from 'rxjs';
import { AuthenticationConnectorService } from './authentication-connector.service';
import * as AuthenticationActions from './store/authentication.actions';
import { fromAuthentication } from './store/authentication.selectors';
import { UserTrackingService } from '@warema/shared/services';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationDataService {
    public user$ = new BehaviorSubject<User | null>(null);

    private readonly _environment = inject<AuthenticationEnvironment>(ENVIRONMENT);
    private readonly _store = inject(Store);
    private readonly _apmService = inject(ApmService);
    private readonly _oauthService = inject(OAuthService);
    private readonly _router = inject(Router);
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _authenticationConnectorService = inject(AuthenticationConnectorService);
    private readonly _userTrackingService = inject(UserTrackingService);

    private _initAlreadyCalled = false;
    private _popupWindowRef: WindowProxy | null = null;
    private _backgroundLoginInterval: ReturnType<typeof setInterval> | null = null;
    private _backgroundLoginSubscription: Subscription | null = null;
    private _backgroundLogoutInterval: ReturnType<typeof setInterval> | null = null;
    private _loadAppSpecificUser: (() => Promise<void>) | null = null;

    // todo: delete after debugging EBIZ-2840
    // eslint-disable-next-line sonarjs/pseudo-random
    public readonly processIdentifier = Math.random();

    constructor() {
        if (this._environment.oauth.showDebugInformation) {
            console.log('EBIZ-2840 Creating authentication data service', this.processIdentifier);
        }
        this._user$.subscribe(user => this.user$.next(user));

        // onDestroy is not called when leaving the page
        window.onbeforeunload = (): void => {
            this._popupWindowRef?.close();
            this._clearBackgroundLoginInterval();
            clearInterval(this._backgroundLogoutInterval);
        };
    }

    public get currentAuthenticationState$(): Observable<CurrentAuthenticationState> {
        return this._store.select(fromAuthentication.selectCurrentAuthenticationState);
    }

    public get authenticationComplete$(): Observable<boolean> {
        return this._store.select(fromAuthentication.selectAuthenticationComplete);
    }

    public get token$(): Observable<Token | null> {
        return this._store.select(fromAuthentication.selectToken);
    }

    private get _user$(): Observable<User | null> {
        return this._store.select(fromAuthentication.selectUser);
    }

    public async init(loadUserCallback?: () => Promise<void>): Promise<void> {
        if (this._initAlreadyCalled) {
            return;
        }

        this._initAlreadyCalled = true;
        this._loadAppSpecificUser = loadUserCallback ?? null;

        // skip first user value, because it is null at the beginning and we don't want to track that
        this.user$.pipe(skip(1)).subscribe(user => {
            this._userTrackingService.trackUserDetails(user?.id, user?.country);
            this._apmService.apm.setUserContext({
                username: user?.email,
                email: user?.email,
                id: user?.id,
            });

            this._apmService.apm.addLabels({
                userId: user?.id,
            });
        });

        // CONFIGURE OAUTH SERVICE AND TOKEN REFRESHING
        this._oauthService.configure(this._environment.oauth);
        this._oauthService.setupAutomaticSilentRefresh();

        // SUBSCRIBE TO IMPORTANT EVENTS
        this._oauthService.events.subscribe(e => {
            if (this._environment.oauth.showDebugInformation) {
                console.log('EBIZ-2840 OAuth event:', e, this.processIdentifier);
            }

            if (e.type === 'session_terminated' || e.type === 'token_refresh_error') {
                void this.logout();
            } else if (e.type === 'token_refreshed') {
                void this._handleTokenUpdate();
            } else if (e.type === 'invalid_nonce_in_state') {
                // Delete nonce from session storage
                sessionStorage.removeItem('nonce');
                void this.logout();
            } else if (e.type === 'token_received') {
                if (this._oauthService.state) {
                    // CHECK IF THERE IS STATE THAT HAS BEEN PASSED TO THE SSO
                    // IF SO WE HAVE TO NAVIGATE TO THAT URL
                    const redirect_uri = decodeURIComponent(this._oauthService.state);
                    if (redirect_uri && redirect_uri !== '/') {
                        void this._router.navigateByUrl(redirect_uri, {
                            onSameUrlNavigation: 'reload',
                            skipLocationChange: false,
                            replaceUrl: true,
                        });
                    }
                } else if (window.location.search.includes('iss=')) {
                    // OTHERWISE CLEAR SSO URL PARAMS.
                    void this._router.navigate([], {
                        relativeTo: this._activatedRoute,
                        queryParams: {},
                        replaceUrl: true,
                    });
                }
            }
        });

        // LOAD DISCOVERY DOCUMENT IF NOT ALREADY DONE
        if (!this._oauthService.discoveryDocumentLoaded) {
            await this._oauthService.loadDiscoveryDocument();
        }

        // DO NOT TRY TO LOGIN WHEN IN LOGOUT SEQUENCE IN POPUP
        if (window.location.href.endsWith('logout')) {
            this._store.dispatch(AuthenticationActions.setFinishedInitializingAction());
            return;
        }

        // CHECK IF THERE IS A CODE IN THE URL HASH OR IF THERE'S LOCAL TOKEN DATA
        await this._oauthService.tryLoginCodeFlow();

        // CHECK IF THERE IS AN VALID TOKEN
        if (this._oauthService.hasValidAccessToken()) {
            await this._handleTokenUpdate();
        } else {
            this._store.dispatch(AuthenticationActions.setUserUnauthenticatedAction());

            // TRY SILENT LOGIN IN BACKGROUND TO NOT DISRUPT USER EXPERIENCE BY BLOCKING APP
            void this._trySilentLogin();
        }

        this._store.dispatch(AuthenticationActions.setFinishedInitializingAction());
    }

    public async login(redirectUri?: string, params?: object | string): Promise<void> {
        const state = await lastValueFrom(this.currentAuthenticationState$.pipe(take(1)));

        // CHECK THE CURRENT AUTHENTICATION STATE
        // ONLY DO THE REDIRECT IF THE USER IS REALLY UNAUTHENTICATED OR IF INITIALIZING, IF USER IS FASTER THAN SILENT LOGIN TIMEOUT
        if (state === 'unauthenticated' || state === 'initializing') {
            // SEND USER TO SSO AND APPEND THE CURRENT PATH AS ADDITIONAL STATE
            const request_uri = window.location.pathname + window.location.search;

            const additionalState = redirectUri ?? (request_uri === '/' ? undefined : request_uri);
            this._oauthService.initCodeFlow(additionalState, params);
        }
    }

    /**
     * this._oauthService.silentRefresh() creates a silentRefreshPostMessageEventListener listening for 'message' events, but does not remove this event listener.
     * this._oauthService.initLoginFlowInPopup() listens for the same 'message' events, in order to get notified when the popup login was successful.
     *
     * We need the silentRefresh call for trying to initally log in the user when they open the app. This leads to a pending event listener.
     * When then logging in through the popup flow, both handlers react to the login and try to authenticate the user. This can lead to this method throwing an error, despite successful login.
     * This is being addressed in B2C-5298
     *
     * https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/oauth-service.ts#L1022
     */
    public async loginWithPopupAndPoll(): Promise<void> {
        try {
            await new Promise<void>((resolve, reject) => {
                // reset interval
                this._clearBackgroundLoginInterval();
                this._removeRegisteredFlag();

                this._backgroundLoginSubscription = this.currentAuthenticationState$
                    .pipe(first(state => state === 'authenticationComplete'))
                    .subscribe(() => {
                        // should be logged in now
                        this._clearBackgroundLoginInterval();
                        this._removeRegisteredFlag();
                        if (this._environment.oauth.showDebugInformation) {
                            console.log('EBIZ-2840 should be logged in now');
                        }
                        resolve();
                    });

                // Start Background Login check
                this._backgroundLoginInterval = setInterval(() => {
                    // when using popup login, you cannot simultaneously silently login using silent refresh, because both functions listen for the same event
                    // instead we set a flag to indicate when a registration was successful and poll for that
                    if (this._environment.oauth.showDebugInformation) {
                        console.log('EBIZ-2840 checking for background login');
                    }

                    if (this._hasValidRegisteredFlag()) {
                        // registered flag found, resolving login
                        if (this._environment.oauth.showDebugInformation) {
                            console.log('EBIZ-2840 registered flag found, resolving login');
                        }

                        this._clearBackgroundLoginInterval();
                        this._removeRegisteredFlag();
                        void this._trySilentLogin(true);
                        resolve();
                    }
                }, 2000);

                void this._loginWithPopup().catch(loginError => {
                    if (this._environment.oauth.showDebugInformation) {
                        console.warn('EBIZ-2840 popup login: signin error', loginError);
                    }
                    this._clearBackgroundLoginInterval();

                    if (loginError instanceof OAuthErrorEvent) {
                        reject(new OAuthError('OAuthError', loginError.type));
                    }
                    reject(loginError instanceof Error ? loginError : new Error(JSON.stringify(loginError)));
                });
            });
        } catch (e) {
            if (this._environment.oauth.showDebugInformation) {
                console.error('EBIZ-2840 Error during login with popup and poll:', e, this.processIdentifier);
            }
            throw e;
        }
    }

    public closeLoginPopup(): void {
        if (this._environment.oauth.showDebugInformation) {
            console.log('EBIZ-2840 close the popup');
        }
        this._popupWindowRef?.close();
        this._clearBackgroundLoginInterval();
    }

    private async _loginWithPopup(): Promise<void> {
        if (this._popupWindowRef?.closed === false) {
            this._popupWindowRef.close();
        }

        this._popupWindowRef = this._openPopup();
        this._popupWindowRef.onbeforeunload = (): void => (this._popupWindowRef = null);

        await this._oauthService.initLoginFlowInPopup({ windowRef: this._popupWindowRef });
    }

    private _clearBackgroundLoginInterval(): void {
        if (this._backgroundLoginInterval !== null) {
            clearInterval(this._backgroundLoginInterval);
            this._backgroundLoginInterval = null;
        }

        if (this._backgroundLoginSubscription !== null) {
            this._backgroundLoginSubscription.unsubscribe();
            this._backgroundLoginSubscription = null;
        }
    }

    public async register(redirectUri?: string): Promise<void> {
        await this.login(redirectUri, { goToRegister: true });
    }

    public async logout(): Promise<void> {
        this._store.dispatch(AuthenticationActions.setUserUnauthenticatedAction());
        await this._oauthService.revokeTokenAndLogout();
    }

    public logoutWithPopup(): void {
        // trigger logout of sso
        this._popupWindowRef = this._openPopup('/logout?startLogout=true');

        this._backgroundLogoutInterval = setInterval(() => {
            if (this._popupWindowRef?.closed) {
                clearInterval(this._backgroundLogoutInterval);
                this._popupWindowRef = null;
                // silently logout frontend
                this._store.dispatch(AuthenticationActions.setUserUnauthenticatedAction());
                void this._oauthService.revokeTokenAndLogout(/* noRedirectToLogoutUrl */ true);
            }
        }, 200);
    }

    /**
     * This seems to be the only way to silently log in, when the user was authenticated at the SSO before.
     * It works by using an iframe to request a new access token without interrupting the user.
     * The SSO redirects the iframe to the silent refresh HTML file, which notifies the app about the new token.
     * This strategy is used in all apps.
     *
     * (the HTML file is different for the configurator, because it is also used in popup authentication)
     */
    private async _trySilentLogin(retry = false): Promise<boolean> {
        if (!this._environment.oauth.silentRefreshRedirectUri) {
            console.error('silent refresh login only works for apps with silent refresh html file');
        }

        if (this._environment.oauth.showDebugInformation) {
            console.log('EBIZ-2840 try silent login');
        }

        if (this._oauthService.hasValidAccessToken()) {
            return true;
        }

        try {
            await this._oauthService.silentRefresh();
            return true;
        } catch {
            if (this._environment.oauth.showDebugInformation) {
                console.error('EBIZ-2840 silent login failed');
            }

            if (retry) {
                // Retry silent login
                return this._trySilentLogin();
            }
            return false;
        }
    }

    public addRegisteredFlag(): void {
        if (this._environment.oauth.showDebugInformation) {
            console.log('EBIZ-2840 adding registered flag');
        }

        localStorage.setItem(StorageConstants.SUCCESSFUL_REGISTRATION_TIMESTAMP, Date.now().toString());
    }

    private _removeRegisteredFlag(): void {
        localStorage.removeItem(StorageConstants.SUCCESSFUL_REGISTRATION_TIMESTAMP);
    }

    private _hasValidRegisteredFlag(): boolean {
        const timestamp = localStorage.getItem(StorageConstants.SUCCESSFUL_REGISTRATION_TIMESTAMP);
        if (!timestamp) {
            return false;
        }

        const registrationTime = Number.parseInt(timestamp, 10);
        const currentTime = Date.now();
        const timeDiff = currentTime - registrationTime;
        const oneDay = 24 * 60 * 60 * 1000;
        if (this._environment.oauth.showDebugInformation) {
            console.log('EBIZ-2840 registered flag validity:', timeDiff < oneDay);
        }
        return timeDiff < oneDay;
    }

    private async _handleTokenUpdate(): Promise<void> {
        const currentToken = this._oauthService.getAccessToken();
        const storedToken = await lastValueFrom(this.token$.pipe(take(1)));
        if (currentToken === storedToken?.token) {
            return;
        }

        const token = {
            token: currentToken,
            validTill: this._oauthService.getAccessTokenExpiration() - 20000,
        };
        this._store.dispatch(AuthenticationActions.setTokenAction({ token }));

        if (this.user$.getValue() === null) {
            void this._loadUser();
        }
    }

    private async _loadUser(): Promise<void> {
        try {
            this._store.dispatch(AuthenticationActions.loadUserAction());

            const ssoUser = (await this._oauthService.loadUserProfile()) as SsoUserInfo;
            const userDto = await lastValueFrom(
                this._authenticationConnectorService.getUserAndAddToApiCache(ssoUser.info.sub),
            );
            const user: User = {
                ...userDto,
                phone: userDto.phoneNumber ?? undefined,
                userType: userDto.userType as UserType,
                isTestUser: false,
            };

            if (this._loadAppSpecificUser !== null) {
                await this._loadAppSpecificUser();
            }

            this._userTrackingService.trackSuccessfullLogin();
            this._store.dispatch(AuthenticationActions.loadUserSuccessAction({ user }));
        } catch (loadUserError) {
            console.error('Error _loadUser', loadUserError);
            this._store.dispatch(AuthenticationActions.loadUserErrorAction({ error: JSON.stringify(loadUserError) }));
        }
    }

    private _openPopup(url?: string | URL): WindowProxy {
        const width = 600;
        const height = 600;
        const left = window.screenLeft + (window.outerWidth - width) / 2;
        const top = window.screenTop + (window.outerHeight - height) / 2;
        return window.open(url, undefined, `popup, height=${height}, width=${width}, left=${left}, top=${top}`);
    }
}
